// Product store – free samples for cart/checkout
/** In-flight promise to dedupe concurrent getFreeSamples calls (Checkout + QuickCart + FreeSamplesBlock) */
let freeSamplesRequest = null
/** Skip redundant updateSelectedSamplesFromCart when cart fires twice on load (getCheckout + getCart) */
let lastProcessedCartItemsLength = null

const state = {
  freeSamples: [],
  allowedFreeSamples: 0,
  /** Number of samples currently selected (from API is_active); kept in sync after getFreeSamples / modal close */
  selectedSampleAmount: 0,
  /** Free samples modal open state (Aside opens, Desktop renders modal) */
  isFreeSamplesModalVisible: false,
  /** Key to remount modal when cart is cleared */
  freeSamplesModalKey: 0,
}

const getters = {
  freeSamples: (state) => state.freeSamples,
  allowedFreeSamples: (state) => state.allowedFreeSamples,
  selectedSampleAmount: (state) => state.selectedSampleAmount,
  /** True when the free samples block should be shown */
  showFreeSamplesBlock: (state) =>
    Boolean(state.allowedFreeSamples && state.freeSamples?.length > 0),
}

const actions = {
  /**
   * Fetches free samples from API and updates store.
   * Deduplicates concurrent calls (Checkout, QuickCart, FreeSamplesBlock can all trigger on refresh).
   * @returns {Promise<{ products: Array, allowed_amount: number }>}
   */
  getFreeSamples({ commit }) {
    if (freeSamplesRequest) {
      return freeSamplesRequest
    }

    freeSamplesRequest = this.api.items
      .getItem('products.free-samples')
      .then(({ data }) => {
        const raw = data?.products ?? []
        const allowed = data?.allowed_amount ?? 0
        const products = raw.map((p) => ({
          ...p,
          id: p.main_product_id ?? p.beauty_deal_id ?? p.id,
          main_image: p.image ? { thumb: p.image } : p.main_image,
          brand_name: p.title ?? p.brand_name,
        }))
        const activeCount = products.filter((p) => p.is_active === true).length
        commit('SET_FREE_SAMPLES', products)
        commit('SET_ALLOWED_FREE_SAMPLES', allowed)
        commit('SET_SELECTED_SAMPLE_AMOUNT', activeCount)
        return data
      })
      .finally(() => {
        freeSamplesRequest = null
      })

    return freeSamplesRequest
  },
  /**
   * Call when cart is loaded or changes. If cart has items, fetches free samples and syncs
   * selectedSampleAmount; if cart is empty, resets selected amount (and optionally sample data).
   */
  updateSelectedSamplesFromCart({ dispatch, commit, state }, cart) {
    const hasItems = cart?.cart_items?.length > 0 || (typeof cart?.item_count === 'number' && cart.item_count > 0)
    const itemCount = cart?.cart_items?.length ?? (hasItems ? cart?.item_count : 0) ?? 0

    if (hasItems) {
      // Skip redundant fetch when cart watcher fires twice on load (getCheckout + getCart) with same cart
      const alreadyHaveData = state.freeSamples?.length > 0 && state.allowedFreeSamples > 0
      if (alreadyHaveData && itemCount === lastProcessedCartItemsLength) {
        return Promise.resolve()
      }
      lastProcessedCartItemsLength = itemCount
      return dispatch('getFreeSamples')
    }
    lastProcessedCartItemsLength = null
    commit('SET_SELECTED_SAMPLE_AMOUNT', 0)
    return Promise.resolve()
  },
  openFreeSamplesModal({ commit }) {
    commit('INCREMENT_FREE_SAMPLES_MODAL_KEY')
    commit('SET_FREE_SAMPLES_MODAL_VISIBLE', true)
  },
  closeFreeSamplesModal({ commit }) {
    commit('SET_FREE_SAMPLES_MODAL_VISIBLE', false)
  },
  incrementFreeSamplesModalKey({ commit }) {
    commit('INCREMENT_FREE_SAMPLES_MODAL_KEY')
  },
}

const mutations = {
  SET_FREE_SAMPLES: (state, payload) => {
    state.freeSamples = payload ?? []
  },
  SET_ALLOWED_FREE_SAMPLES: (state, payload) => {
    state.allowedFreeSamples = payload ?? 0
  },
  SET_SELECTED_SAMPLE_AMOUNT: (state, payload) => {
    state.selectedSampleAmount = payload ?? 0
  },
  SET_FREE_SAMPLES_MODAL_VISIBLE: (state, payload) => {
    state.isFreeSamplesModalVisible = Boolean(payload)
  },
  INCREMENT_FREE_SAMPLES_MODAL_KEY: (state) => {
    state.freeSamplesModalKey += 1
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
